import DefaultViewManager from 'epubjs/src/managers/default';
import { EVENTS } from 'epubjs/src/utils/constants';

const SLIDE_DURATION_MS = 400;

const easeInOut = (progress) => (progress < 0.5 ? 4 * progress ** 3 : 1 - (-2 * progress + 2) ** 3 / 2);

/**
 * Slides pages horizontally for reflowable books in paginated mode.
 *
 * epub.js renders each reflowable chapter as one wide iframe split into page-sized columns. It shows one page at a
 * time by scrolling the container, and it only ever keeps one chapter in the container.
 *
 * - Turning a page inside a chapter: the next page is already rendered beside the current one. We do the same scroll
 *   epub.js does, then use a transform to start the chapter where it was before the scroll and slide it into place.
 * - Turning a page at the start or end of a chapter: the page we need is in the chapter next to the current one.
 *   We load that chapter beside the current one, slide both, then remove the old chapter and scroll exactly like
 *   epub.js does when it switches chapters.
 *
 * Like the FlipperManager, a page turn runs on its own: next() and prev() start it and return straight away, so the
 * rendition queue does not hold further turns behind it. Turns requested while one is in progress are ignored.
 *
 * Everything else (display(), resizing, scrolled flow, vertical writing, hidden tabs) is left to epub.js.
 */
class ReflowableSliderManager extends DefaultViewManager {
  constructor(options) {
    super(options);
    this.name = 'reflowableslider';
    // True for the whole page turn, including the time spent loading the chapter next to the current one, which can
    // take a while. Also read by the rendition store, to ignore page turns requested meanwhile.
    this.isTurningPage = false;
    this.stopCurrentTurn = null;
  }

  next() {
    return this.turnPage('forward');
  }

  prev() {
    return this.turnPage('backward');
  }

  /**
   * epub.js clears all views on resize and before display(). A page turn in progress (loading or sliding) must stop
   * first, otherwise it would keep working on views that no longer exist.
   */
  clear() {
    if (this.stopCurrentTurn) {
      this.stopCurrentTurn();
    }
    super.clear();
  }

  /**
   * display() (table of contents, bookmarks, audio jumps) can arrive during a page turn, because the turn does not hold
   * the rendition queue. Clearing first stops the turn and lets epub.js render the target from scratch.
   */
  display(section, target) {
    if (this.isTurningPage) {
      this.clear();
    }
    return super.display(section, target);
  }

  /**
   * While a page is turning, epub.js would read the location from pages that are still moving, or from the old chapter
   * while the next one loads. rendition.next() asks for it right after next() returns, and ignored turns ask too.
   * Returning no location makes the rendition skip those reports. We report once the turn is done instead, so
   * "relocated" fires once per page turn.
   */
  currentLocation() {
    if (this.isTurningPage) {
      return [];
    }
    return super.currentLocation();
  }

  /**
   * The rendition reports the current location when the manager emits "scrolled".
   */
  reportLocationAfterTurn() {
    if (this.rendered) {
      this.emit(EVENTS.MANAGERS.SCROLLED, { top: this.container.scrollTop, left: this.container.scrollLeft });
    }
  }

  canSlide() {
    return this.isPaginated && this.settings.axis === 'horizontal' && this.views.length === 1 && !document.hidden;
  }

  turnPage(direction) {
    if (this.isTurningPage) {
      return undefined;
    }

    if (!this.canSlide()) {
      return direction === 'forward' ? super.next() : super.prev();
    }

    let theSectionNextToTheCurrentOne = null;
    if (!this.hasAnotherPageInThisChapter(direction)) {
      const currentSection = this.views.first().section;
      theSectionNextToTheCurrentOne = direction === 'forward' ? currentSection.next() : currentSection.prev();
      if (!theSectionNextToTheCurrentOne) {
        return undefined;
      }
    }

    this.isTurningPage = true;
    const turn = theSectionNextToTheCurrentOne
      ? this.slideToTheChapterNextToTheCurrentOne(theSectionNextToTheCurrentOne, direction)
      : this.slideInsideChapter(direction);

    // When clear() stopped the turn (resize, display(), closing the book), epub.js reports the location itself.
    turn
      .then((shouldReportLocation) => {
        if (shouldReportLocation) {
          this.reportLocationAfterTurn();
        }
      })
      .finally(() => {
        this.isTurningPage = false;
      });

    return undefined;
  }

  /**
   * Same checks epub.js uses in DefaultViewManager next() and prev() to decide between scrolling and switching
   * chapters, including its handling of the browser's right-to-left scroll type.
   */
  hasAnotherPageInThisChapter(direction) {
    const { scrollLeft, scrollWidth, offsetWidth } = this.container;
    const pageWidth = this.layout.delta;

    if (this.settings.direction !== 'rtl') {
      return direction === 'forward' ? scrollLeft + offsetWidth + pageWidth <= scrollWidth : scrollLeft > 0;
    }

    if (this.settings.rtlScrollType === 'default') {
      return direction === 'forward' ? scrollLeft > 0 : scrollLeft + offsetWidth < scrollWidth;
    }

    return direction === 'forward' ? scrollLeft - pageWidth > -scrollWidth : scrollLeft < 0;
  }

  /**
   * How far, in pixels, the content moves on screen when turning a page. Negative means it moves left.
   */
  getSlideDistance(direction) {
    const towardsStartOfChapter = direction === 'forward' ? -1 : 1;
    const readingDirection = this.settings.direction === 'rtl' ? -1 : 1;

    return towardsStartOfChapter * readingDirection * this.layout.delta;
  }

  /**
   * Resolves with false when clear() stopped the slide.
   */
  async slideInsideChapter(direction) {
    const view = this.views.first();
    const scrollBefore = this.container.scrollLeft;
    this.scrollBy(direction === 'forward' ? this.layout.delta : -this.layout.delta, 0, true);
    const scrolledBy = this.container.scrollLeft - scrollBefore;

    const finished = await this.playSlide((progress) => {
      setHorizontalOffset(view.element, scrolledBy * (1 - progress));
    });

    setHorizontalOffset(view.element, 0);
    return finished;
  }

  /**
   * Resolves with false when clear() stopped the turn.
   * When the chapter fails to load, the reader stays on the current page and the error is thrown again, so it reaches
   * Sentry.
   */
  async slideToTheChapterNextToTheCurrentOne(section, direction) {
    const oldView = this.views.first();
    let newView;
    try {
      newView = await this.loadChapterNextToCurrent(section);
    } catch (error) {
      const failedView = this.views.last();
      if (failedView && failedView !== oldView) {
        this.views.remove(failedView);
      }
      // The stores only finish a page turn (e.g. reset isChangingPage) when the location is reported.
      this.reportLocationAfterTurn();
      throw error;
    }

    const wasClearedWhileLoading = !newView || !this.rendered || this.views.indexOf(oldView) === -1;
    if (wasClearedWhileLoading) {
      return false;
    }

    // A chapter with vertical writing switches epub.js to vertical scrolling. We can not slide that horizontally, so
    // we switch chapters without animation, starting at the top like epub.js does after clear().
    if (this.settings.axis !== 'horizontal') {
      this.views.remove(oldView);
      this.scrollTo(0, 0, true);
      newView.show();
      return true;
    }

    const slideDistance = this.getSlideDistance(direction);
    const showFirstPage = direction === 'forward';
    let currentProgress = 0;

    const placeBothChapters = () => {
      setHorizontalOffset(oldView.element, slideDistance * currentProgress);
      const offsetThatShowsTargetPage = this.getOffsetThatShowsPage(newView, showFirstPage);
      setHorizontalOffset(newView.element, offsetThatShowsTargetPage - slideDistance * (1 - currentProgress));
    };

    // Themes, our content hooks and late fonts can still change a chapter's width while it slides. epub.js resizes
    // the chapter in its own animation frame, which can run after ours and before the browser paints. Placing the
    // chapters again right after each resize stops the new page from flashing on screen for a frame.
    newView.on(EVENTS.VIEWS.RESIZED, placeBothChapters);
    oldView.on(EVENTS.VIEWS.RESIZED, placeBothChapters);

    newView.show();
    const finished = await this.playSlide((progress) => {
      currentProgress = progress;
      placeBothChapters();
    });

    newView.off(EVENTS.VIEWS.RESIZED, placeBothChapters);
    oldView.off(EVENTS.VIEWS.RESIZED, placeBothChapters);

    if (!finished) {
      return false;
    }

    this.views.remove(oldView);
    setHorizontalOffset(newView.element, 0);

    if (showFirstPage) {
      this.scrollTo(0, 0, true);
    } else {
      this.scrollToLastPage();
      // Like epub.js prepend(): if the chapter grows later (e.g. a font finishes loading), stay on its last page.
      newView.on(EVENTS.VIEWS.RESIZED, (bounds) => this.counter(bounds));
    }
    return true;
  }

  /**
   * Adds the chapter after the current one in the container. It stays hidden and off screen while it loads, so the
   * reader keeps seeing the current page. Loading can take a while on slow connections, so there is no time limit:
   * like epub.js, we wait for it. Page turns requested meanwhile are ignored, and clear() (resize, display(), closing
   * the book) stops the wait. Resolves with the view, or null when it was stopped. Rejects when loading failed.
   */
  loadChapterNextToCurrent(section) {
    const stopped = new Promise((resolve) => {
      this.stopCurrentTurn = () => resolve(null);
    });

    return Promise.race([this.append(section), stopped]).finally(() => {
      this.stopCurrentTurn = null;
    });
  }

  /**
   * The horizontal offset that puts the first or last page of a view exactly in the visible area.
   * Uses layout positions (offsetLeft, scrollLeft) instead of getBoundingClientRect(), so it is not affected by our
   * own transforms or by the player zoom.
   */
  getOffsetThatShowsPage(view, showFirstPage) {
    const { offsetLeft, offsetWidth } = view.element;
    const leftPageLeft = offsetLeft;
    const rightPageLeft = offsetLeft + offsetWidth - this.layout.delta;
    const isRTL = this.settings.direction === 'rtl';

    let pageLeft;
    if (showFirstPage) {
      pageLeft = isRTL ? rightPageLeft : leftPageLeft;
    } else {
      pageLeft = isRTL ? leftPageLeft : rightPageLeft;
    }

    return this.container.scrollLeft - pageLeft;
  }

  /**
   * Same final scroll as epub.js DefaultViewManager prev() after it loads the previous chapter.
   */
  scrollToLastPage() {
    if (this.settings.direction !== 'rtl') {
      this.scrollTo(this.container.scrollWidth - this.layout.delta, 0, true);
    } else if (this.settings.rtlScrollType === 'default') {
      this.scrollTo(0, 0, true);
    } else {
      this.scrollTo(this.container.scrollWidth * -1 + this.layout.delta, 0, true);
    }
  }

  /**
   * Calls drawFrame with a progress from 0 to 1 on every animation frame.
   * Resolves with true when the slide finished, or false when clear() stopped it.
   */
  playSlide(drawFrame) {
    return new Promise((resolve) => {
      const startTime = performance.now();
      let frameId = null;
      let safetyTimer = null;

      const end = (finished) => {
        cancelAnimationFrame(frameId);
        clearTimeout(safetyTimer);
        this.stopCurrentTurn = null;
        resolve(finished);
      };

      const onFrame = (now) => {
        const progress = Math.min(1, Math.max(0, (now - startTime) / SLIDE_DURATION_MS));
        drawFrame(easeInOut(progress));

        if (progress < 1) {
          frameId = requestAnimationFrame(onFrame);
        } else {
          end(true);
        }
      };

      drawFrame(0);
      frameId = requestAnimationFrame(onFrame);
      // Browsers pause requestAnimationFrame in background tabs. Without this, a slide that started just before the tab
      // was hidden would keep isTurningPage true, and background audio could not turn pages until the tab is visible.
      safetyTimer = setTimeout(() => end(true), SLIDE_DURATION_MS + 500);
      this.stopCurrentTurn = () => end(false);
    });
  }
}

/**
 * Pages are left at translate3d(0) after a slide instead of removing the transform. The browser then keeps the page
 * on its own compositing layer, instead of dropping it and creating it again on every turn.
 */
const setHorizontalOffset = (element, offset) => {
  if (!element) {
    return;
  }
  element.style.transform = `translate3d(${offset}px, 0, 0)`;
};

export default ReflowableSliderManager;
